import { useState } from "react";
import { http, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SectionHeader from "@/components/SectionHeader";
import PageSEO from "@/components/PageSEO";

const CATEGORIES = ["Financial Institutions", "Fintech Companies", "Real Estate Companies", "Agricultural Businesses", "Technology Companies", "Corporate Organisations", "Media Organisations", "Event Sponsors", "Strategic Investors"];

export default function Partnerships() {
  const [form, setForm] = useState({ company_name: "", contact_person: "", email: "", phone: "", industry: "", partnership_type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await http.post("/partnership", form);
      toast.success(data?.message || "Thanks — we'll be in touch.");
      setForm({ company_name: "", contact_person: "", email: "", phone: "", industry: "", partnership_type: "", message: "" });
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <PageSEO
        title="Partner With Mayoto"
        description="Mayoto welcomes partnerships with financial institutions, fintechs, real estate developers, agribusinesses and strategic investors. Let's build long-term value together."
        canonicalPath="/partnerships"
      />
      <section className="mayo-grain">
        <div className="container-wide py-20 lg:py-24">
          <p className="text-[#00b4d8] font-display font-bold tracking-[0.25em] text-xs">PARTNERSHIPS</p>
          <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-[#0a1128] max-w-3xl" data-testid="partnerships-title">Let's Build the Future Together.</h1>
          <p className="mt-5 text-slate-600 max-w-2xl leading-relaxed">
            Mayoto believes strong partnerships create stronger opportunities. We welcome strategic relationships with organisations that share our commitment to innovation, economic growth, and sustainable value creation.
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container-wide">
          <SectionHeader eyebrow="PARTNERSHIP CATEGORIES" title="Who we partner with." />
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CATEGORIES.map(c => (
              <div key={c} className="card-lift bg-white border border-slate-200 rounded-2xl p-6">
                <p className="font-display font-bold text-[#0a1128]">{c}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#f8fafc]">
        <div className="container-wide max-w-3xl">
          <SectionHeader eyebrow="ENQUIRY" title="Start a Partnership Conversation." description="Share a few details and our partnerships team will follow up." />
          <form onSubmit={submit} className="mt-10 grid sm:grid-cols-2 gap-5 bg-white border border-slate-200 rounded-3xl p-8" data-testid="partnership-form">
            <div><Label>Company Name</Label><Input required value={form.company_name} onChange={e => set("company_name", e.target.value)} data-testid="partnership-company" /></div>
            <div><Label>Contact Person</Label><Input required value={form.contact_person} onChange={e => set("contact_person", e.target.value)} data-testid="partnership-person" /></div>
            <div><Label>Email</Label><Input required type="email" value={form.email} onChange={e => set("email", e.target.value)} data-testid="partnership-email" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e => set("phone", e.target.value)} data-testid="partnership-phone" /></div>
            <div><Label>Industry</Label><Input required value={form.industry} onChange={e => set("industry", e.target.value)} data-testid="partnership-industry" /></div>
            <div>
              <Label>Partnership Type</Label>
              <Select value={form.partnership_type} onValueChange={v => set("partnership_type", v)}>
                <SelectTrigger data-testid="partnership-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>Message</Label><Textarea required rows={5} value={form.message} onChange={e => set("message", e.target.value)} data-testid="partnership-message" /></div>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={loading} className="btn-shine bg-[#00b4d8] hover:bg-[#0096c7] rounded-full px-7 py-6 text-base w-full sm:w-auto" data-testid="partnership-submit">
                {loading ? "Sending…" : "Start a Partnership Conversation"}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
